import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faPenToSquare, 
  faTrash, 
  faEye,
  faSave,
  faTimes,
  faCode
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import './Posts.css';

const Posts = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualUrl, setManualUrl] = useState(''); // Separate state for manual URL input
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    category_id: '',
    status: 'draft',
    featured_image: ''
  });

  // Load posts and categories
  useEffect(() => {
    loadPosts();
    loadCategories();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await apiService.getPosts();
      setPosts(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
      setError('Failed to load posts');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories({ paginate: false });
      // Handle different response structures
      const categoriesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]); // Ensure it's always an array
    }
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      category_id: '',
      status: 'draft',
      featured_image: ''
    });
    setManualUrl('');
    setShowEditor(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content || '',
      excerpt: post.excerpt || '',
      category_id: post.category_id || '',
      status: post.status || 'draft',
      featured_image: post.featured_image || ''
    });
    setManualUrl(post.featured_image || '');
    setShowEditor(true);
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await apiService.deletePost(postId);
        setPosts(posts.filter(p => p.id !== postId));
      } catch (error) {
        console.error('Error deleting post:', error);
        setError('Failed to delete post');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (editingPost) {
        // Update existing post
        const response = await apiService.updatePost(editingPost.id, formData);
        setPosts(posts.map(p => 
          p.id === editingPost.id 
            ? response.data.post 
            : p
        ));
      } else {
        // Create new post
        const response = await apiService.createPost(formData);
        setPosts([response.data.post, ...posts]);
      }
      
      setShowEditor(false);
      setEditingPost(null);
    } catch (error) {
      console.error('Error saving post:', error);
      setError(error.response?.data?.message || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !editingPost) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const response = await apiService.uploadImage(file);
      // Prefer backend-provided URL, but fall back to building from path if needed
      const urlFromApi = response?.data?.url || '';
      const pathFromApi = response?.data?.path || '';
      const isValidImageUrl = (u) => {
        if (!u) return false;
        if (u.endsWith('/')) return false;
        return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(u) || u.startsWith('data:image/');
      };
      const builtUrl = pathFromApi ? `${window.location.origin}/storage/${pathFromApi}` : '';
      return isValidImageUrl(urlFromApi) ? urlFromApi : builtUrl || urlFromApi;
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  };

  const handlePreviewPost = (post) => {
    const postUrl = `https://blog.bonlineco.com/post/${post.slug}`;
    window.open(postUrl, '_blank');
  };

  if (showEditor) {
    return (
      <div className="posts-editor">
        <div className="posts-editor-header">
          <h2>
            <FontAwesomeIcon icon={faPenToSquare} style={{ marginRight: '12px', color: '#6366f1' }} />
            {editingPost ? 'Edit Post' : 'Create New Post'}
          </h2>
          <button 
            type="button" 
            onClick={() => setShowEditor(false)}
            className="posts-btn posts-btn-secondary"
          >
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
            Cancel
          </button>
        </div>

        <form onSubmit={handleSubmit} className="posts-form">
          <div className="posts-form-row">
            <div className="posts-form-group posts-form-group-full">
              <label htmlFor="title">Post Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter post title"
                required
              />
            </div>
          </div>

          <div className="posts-form-row">
            <div className="posts-form-group">
              <label htmlFor="slug">Slug *</label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="post-url-slug"
                required
              />
            </div>

            <div className="posts-form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="posts-form-group posts-form-group-full">
            <label htmlFor="excerpt">Excerpt</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Brief description of the post"
              rows="3"
            />
          </div>

          <div className="posts-form-group posts-form-group-full">
            <label htmlFor="content">
              <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px', color: '#8b5cf6' }} />
              HTML Content *
            </label>
            <div className="posts-html-editor-info">
              <p>Paste your HTML content below. You can use AI-generated HTML directly.</p>
            </div>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="<h1>Your HTML content here...</h1>
<p>Use any HTML tags you need. This editor accepts full HTML markup.</p>"
              rows="20"
              className="posts-html-editor"
              required
            />
          </div>

          <div className="posts-form-group posts-form-group-full">
            <label>Featured Image</label>
            <ImageUpload
              value={formData.featured_image}
              onChange={(url) => {
                setFormData(prev => ({ ...prev, featured_image: url }));
                setManualUrl(url);
              }}
              onUpload={handleImageUpload}
            />
            <small style={{ color: '#64748b', fontSize: '13px', marginTop: '8px', display: 'block' }}>
              Or paste image URL below:
            </small>
            <input
              type="url"
              name="featured_image_manual"
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              onBlur={(e) => {
                const url = e.target.value.trim();
                if (url === '' || url.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i)) {
                  setFormData(prev => ({ ...prev, featured_image: url }));
                }
              }}
              placeholder="https://example.com/image.jpg"
              style={{ marginTop: '8px' }}
            />
          </div>

          <div className="posts-form-row">
            <div className="posts-form-group" style={{ display: 'none' }}>
              {/* Hidden field for layout */}
            </div>

            <div className="posts-form-group">
              <label htmlFor="category_id">Category</label>
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                {Array.isArray(categories) && categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="posts-error">{error}</div>
          )}

          <div className="posts-form-actions">
            {editingPost && (
              <button 
                type="button" 
                onClick={() => handlePreviewPost(editingPost)}
                className="posts-btn posts-btn-preview"
              >
                <FontAwesomeIcon icon={faEye} style={{ marginRight: '8px' }} />
                View Live Post
              </button>
            )}
            <button 
              type="submit" 
              className="posts-btn posts-btn-primary"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} />
              {loading ? 'Saving...' : (editingPost ? 'Update Post' : 'Create Post')}
            </button>
            <button 
              type="button" 
              onClick={() => setShowEditor(false)}
              className="posts-btn posts-btn-secondary"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="posts-page">
      <div className="posts-header">
        <div>
          <h2>Posts Management</h2>
          <p>Create and manage your blog posts</p>
        </div>
        <button onClick={handleNewPost} className="posts-btn posts-btn-primary">
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
          New Post
        </button>
      </div>

      <div className="posts-table-container">
        <table className="posts-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 ? (
              <tr>
                <td colSpan="5" className="posts-empty">
                  No posts yet. Create your first post!
                </td>
              </tr>
            ) : (
              posts.map(post => (
                <tr key={post.id}>
                  <td>
                    <div className="posts-title">
                      {post.title}
                      {post.excerpt && (
                        <div className="posts-excerpt">{post.excerpt}</div>
                      )}
                    </div>
                  </td>
                  <td className="posts-slug">{post.slug}</td>
                  <td>
                    <span className={`posts-status posts-status-${post.status}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="posts-date">{post.created_at}</td>
                  <td>
                    <div className="posts-actions">
                      <button
                        onClick={() => handleEditPost(post)}
                        className="posts-action-btn posts-action-edit"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                      <button
                        onClick={() => handlePreviewPost(post)}
                        className="posts-action-btn posts-action-view"
                        title="Preview"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="posts-action-btn posts-action-delete"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Posts;
